import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Define a interface para o usuário
interface UserWithoutPassword {
  username: string;
}

// Define a interface para o contexto de autenticação
interface AuthContextType {
  isAuthenticated: boolean;
  user: UserWithoutPassword | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  users: UserWithoutPassword[];
  addUser: (username: string, password: string) => Promise<boolean>;
  updateUser: (oldUsername: string, newUsername: string, newPassword?: string) => Promise<boolean>;
  deleteUser: (username: string) => Promise<boolean>;
  loading: boolean;
}

// Cria o contexto com valores padrão
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CURRENT_USER_KEY = 'currentUser';

const getStoredUser = (): UserWithoutPassword | null => {
  try {
    const stored = localStorage.getItem(CURRENT_USER_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as UserWithoutPassword;
  } catch {
    return null;
  }
};

const setStoredUser = (user: UserWithoutPassword | null) => {
  if (!user) {
    localStorage.removeItem(CURRENT_USER_KEY);
    return;
  }
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
};

// Define o provedor de autenticação
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserWithoutPassword | null>(null);
  const [users, setUsers] = useState<UserWithoutPassword[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Efeito para verificar autenticação ao iniciar (cookies são enviados automaticamente)
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Tenta usar o usuário em cache enquanto verifica com o servidor
        const cachedUser = getStoredUser();
        if (cachedUser) {
          setUser(cachedUser);
        }

        // Verifica se o cookie é válido chamando /auth/me
        // O cookie HTTP-only é enviado automaticamente pelo axios (withCredentials)
        const me = await api.me();
        const current = { username: me.username };
        setUser(current);
        setIsAuthenticated(true);
        setStoredUser(current);

        // Carrega lista de usuários
        try {
          const allUsers = await api.getAllUsers();
          setUsers(allUsers.map((u: any) => ({ username: u.username })));
        } catch (e) {
          console.warn('Falha ao carregar lista de usuários:', e);
        }
      } catch (error: any) {
        // Cookie inválido ou expirado - limpa estado
        console.log('Usuário não autenticado ou sessão expirada', error?.message || '');
        setIsAuthenticated(false);
        setUser(null);
        setStoredUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  // Função de login
  const login = async (usernameInput: string, passwordInput: string): Promise<boolean> => {
    try {
      const response = await api.login(usernameInput, passwordInput);
      const loggedUser = { username: response.user.username };
      setUser(loggedUser);
      setIsAuthenticated(true);
      setStoredUser(loggedUser);

      const allUsers = await api.getAllUsers();
      setUsers(allUsers.map((u: any) => ({ username: u.username })));
      return true;
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      return false;
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
    setIsAuthenticated(false);
    setUser(null);
    setStoredUser(null);
  };

  // Função para adicionar um novo usuário
  const addUser = async (username: string, password: string): Promise<boolean> => {
    try {
      await api.register(username, password);
      const allUsers = await api.getAllUsers();
      setUsers(allUsers.map((u: any) => ({ username: u.username })));
      return true;
    } catch (error) {
      console.error('Erro ao adicionar usuário:', error);
      return false;
    }
  };

  // Função para atualizar um usuário existente
  const updateUser = async (oldUsername: string, newUsername: string, newPassword?: string): Promise<boolean> => {
    try {
      await api.updateUser(oldUsername, { newUsername, newPassword });
      const allUsers = await api.getAllUsers();
      setUsers(allUsers.map((u: any) => ({ username: u.username })));

      if (user && user.username === oldUsername) {
        const updated = { username: newUsername };
        setUser(updated);
        setStoredUser(updated);
      }
      return true;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      return false;
    }
  };

  // Função para excluir um usuário
  const deleteUser = async (username: string): Promise<boolean> => {
    try {
      await api.deleteUser(username);
      const allUsers = await api.getAllUsers();
      setUsers(allUsers.map((u: any) => ({ username: u.username })));
      return true;
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        users,
        addUser,
        updateUser,
        deleteUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
